import { createAsyncThunk } from "@reduxjs/toolkit";
import { ScheduleAssistMessageRequest } from "../contracts/ScheduleAssistMessageRequest";
import { ScheduleAssistReplyResponse } from "../contracts/ScheduleAssistReplyResponse";
import { ConfirmSchedulePlanRequest } from "../contracts/ConfirmSchedulePlanRequest";
import { ConfirmSchedulePlanResponse } from "../contracts/ConfirmSchedulePlanResponse";
import { AssistantChatMessage } from "../models/AssistantChatMessage";
import { SchedulePlanAction } from "../models/SchedulePlanAction";
import { PlanResult } from "../models/PlanResult";
import {
  AssistantReply,
  ScheduleAssistReplyResponseMapper,
} from "../mappers/ScheduleAssistReplyResponseMapper";
import { ConfirmSchedulePlanResponseMapper } from "../mappers/ConfirmSchedulePlanResponseMapper";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { fetchGroups, fetchMembers } from "../../members/slice-extensions/members-slice-extension";

const replyMapper = new ScheduleAssistReplyResponseMapper();
const resultMapper = new ConfirmSchedulePlanResponseMapper();

interface AssistantScheduleSliceState {
  assistantSchedule: { messages: AssistantChatMessage[] };
}

export const sendAssistantMessage = createAsyncThunk<
  AssistantReply,
  string,
  { rejectValue: ApiErrorResponse }
>(
  "assistantSchedule/sendMessage",
  async (_text: string, { getState, rejectWithValue }) => {
    try {
      // The pending reducer already appended the user's message (meta.arg) to state.
      const messages = (getState() as AssistantScheduleSliceState).assistantSchedule.messages;
      const request: ScheduleAssistMessageRequest = {
        messages: messages.map((message) => ({ role: message.role, text: message.text })),
      };
      const response = await axiosInstance.post<ScheduleAssistReplyResponse>(
        "/assistant/schedule/message",
        request,
      );
      return replyMapper.toAssistantReply(response.data);
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error, "The assistant could not answer"));
    }
  },
);

export const confirmSchedulePlan = createAsyncThunk<
  PlanResult,
  SchedulePlanAction[],
  { rejectValue: ApiErrorResponse }
>(
  "assistantSchedule/confirmPlan",
  async (actions: SchedulePlanAction[], { dispatch, rejectWithValue }) => {
    try {
      const request: ConfirmSchedulePlanRequest = { actions };
      const response = await axiosInstance.post<ConfirmSchedulePlanResponse>(
        "/assistant/schedule/confirm",
        request,
      );
      // The members/groups directory is loaded once per session; refresh it so
      // plan-created entities resolve without a page reload.
      dispatch(fetchMembers());
      dispatch(fetchGroups());
      return resultMapper.toPlanResult(response.data);
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error, "Failed to apply the plan"));
    }
  },
);
